/** Dev21 — Event Schedule. Image-led cards for each celebration event. */

import React from 'react';
import { motion } from 'motion/react';
import { CalendarDays, Clock, MapPin } from 'lucide-react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';

export function ScheduleSection({
  content,
}: {
  content: Dev21Content['schedule'];
}) {
  return (
    <Section id="schedule" tone="surface">
      <SectionHeading label={content.label} title={content.title} />

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {content.events.map((event, i) => (
          <Reveal key={`${event.title}-${i}`} delay={i * 0.12}>
            <motion.article
              whileHover={{ y: -10 }}
              transition={{ duration: 0.4, ease: DEV21_EASE }}
              className="flex h-full flex-col overflow-hidden rounded-3xl border border-[var(--d21-border)] bg-[var(--d21-surface)] shadow-[0_28px_64px_-44px_rgba(33,26,19,0.55)]"
            >
              {event.image ? (
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--d21-accent)] text-sm font-semibold text-white shadow-lg">
                    {i + 1}
                  </span>
                </div>
              ) : null}

              <div className="flex flex-1 flex-col gap-3 p-6">
                <h3 className="font-serif text-xl font-semibold text-[var(--d21-ink)]">
                  {event.title}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--d21-muted)]">
                  {event.description}
                </p>
                <ul className="mt-auto flex flex-col gap-2 border-t border-[var(--d21-border)] pt-4 text-sm text-[var(--d21-ink)]">
                  <li className="flex items-center gap-2">
                    <CalendarDays size={15} className="text-[var(--d21-accent)]" />
                    {event.date}
                  </li>
                  {event.time ? (
                    <li className="flex items-center gap-2">
                      <Clock size={15} className="text-[var(--d21-accent)]" />
                      {event.time}
                    </li>
                  ) : null}
                  <li className="flex items-center gap-2">
                    <MapPin size={15} className="text-[var(--d21-accent)]" />
                    {event.venue}
                  </li>
                </ul>
              </div>
            </motion.article>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
